import * as React from 'react';
import { format } from 'date-fns';
import { CalendarDays, Clock, MapPin, UserRound, Video } from 'lucide-react';
import { Button } from '../core/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../core/dialog';
import { getJoinState } from './event-utils';

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Everything the card renders. Times are ISO UTC strings; the card formats
 * them in the viewer's local timezone via date-fns.
 */
export type EventDetailData = {
  title: string;
  /** Already-translated kind label, e.g. "Group class". */
  kindLabel: string;
  /** Optional secondary line under the title, e.g. the event category. */
  categoryLine?: string | null;
  startTime: string;
  endTime?: string | null;
  hostName?: string | null;
  hostAvatarUrl?: string | null;
  /** Human-readable location (URLs stripped — see stripUrlFromLocation). */
  locationText?: string | null;
  description?: string | null;
  /** Meeting URL; the join button renders only inside the join window. */
  joinUrl?: string | null;
};

export type EventDetailCardLabels = {
  date: string;
  time: string;
  teacher: string;
  location: string;
  join: string;
  goToClass: string;
  live: string;
};

export type EventDetailCardProps = {
  /** Null renders nothing — pass the selected item or null. */
  event: EventDetailData | null;
  labels: EventDetailCardLabels;
  onClose: () => void;
  /**
   * Router-agnostic class action: when provided, the outline "go to class"
   * button renders and calls it (the consumer navigates and/or closes).
   */
  onGoToClass?: (() => void) | null;
};

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-50 text-[#cf2e2e]">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-sm font-medium text-gray-900">{children}</p>
      </div>
    </div>
  );
}

/**
 * Shared read-only event detail card (design "Variant B — Solid Red"):
 * a red gradient header band with the kind pill, live chip and title, then a
 * white body with rose meta rows, description and right-aligned actions.
 * Presentational and label-driven — the same card backs the p2e calendar,
 * the dashboard agenda and the teacher agenda.
 */
export function EventDetailCard({ event, labels, onClose, onGoToClass }: EventDetailCardProps) {
  if (!event) return null;

  const start = new Date(event.startTime);
  const end = event.endTime ? new Date(event.endTime) : null;
  const nowMs = Date.now();
  const endMs = end ? end.getTime() : start.getTime() + ONE_HOUR_MS;
  const isLive = start.getTime() <= nowMs && nowMs < endMs;
  // Join CTA only from 1 hour before start until the event ends.
  const canJoin = Boolean(event.joinUrl) && getJoinState(event.startTime, event.endTime) !== null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-md overflow-hidden rounded-3xl border-rose-100 p-0 [&>button]:text-white [&>button]:focus:ring-white/60">
        {/* Solid red header band */}
        <DialogHeader className="space-y-0 bg-gradient-to-br from-[#d63434] to-[#b82222] px-6 pb-5 pt-6 text-left">
          <div className="flex flex-wrap items-start justify-between gap-2 pr-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-white">
                {event.kindLabel}
              </span>
            </span>
            {isLive && (
              <span className="inline-flex animate-pulse items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#cf2e2e] motion-reduce:animate-none">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
                {labels.live}
              </span>
            )}
          </div>
          <DialogTitle className="pt-3 text-xl font-semibold text-white">{event.title}</DialogTitle>
          {event.categoryLine ? (
            <DialogDescription className="pt-0.5 text-[11px] uppercase tracking-wide text-white/75">
              {event.categoryLine}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        {/* White body */}
        <div className="space-y-3 px-6 pb-6 pt-1 text-sm text-gray-700">
          <DetailRow icon={<CalendarDays className="h-4 w-4" />} label={labels.date}>
            {format(start, 'EEEE, MMMM d, yyyy')}
          </DetailRow>
          <DetailRow icon={<Clock className="h-4 w-4" />} label={labels.time}>
            {format(start, 'h:mm a')}
            {end ? ` – ${format(end, 'h:mm a')}` : null}
          </DetailRow>
          {event.hostName && (
            <DetailRow
              icon={
                event.hostAvatarUrl ? (
                  <img
                    src={event.hostAvatarUrl}
                    alt=""
                    aria-hidden="true"
                    className="h-5 w-5 rounded-full object-cover"
                  />
                ) : (
                  <UserRound className="h-4 w-4" />
                )
              }
              label={labels.teacher}
            >
              {event.hostName}
            </DetailRow>
          )}
          {event.locationText && (
            <DetailRow icon={<MapPin className="h-4 w-4" />} label={labels.location}>
              {event.locationText}
            </DetailRow>
          )}
          {event.description && (
            <p className="rounded-2xl bg-rose-50/60 p-3 text-sm leading-relaxed text-gray-600">
              {event.description}
            </p>
          )}

          {(onGoToClass || canJoin) && (
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {onGoToClass && (
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full border-rose-200 text-[#cf2e2e] hover:bg-rose-50"
                  onClick={onGoToClass}
                >
                  {labels.goToClass}
                </Button>
              )}
              {canJoin && event.joinUrl && (
                <Button asChild className="rounded-full bg-[#cf2e2e] text-white hover:bg-[#b32323]">
                  <a href={event.joinUrl} target="_blank" rel="noopener noreferrer">
                    <Video className="mr-1.5 h-4 w-4" />
                    {labels.join}
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
